import { FormControl, MenuItem, Select, Slider, Typography, TextField, Checkbox, ListItemText } from '@mui/material';
import { ReactComponent as BedOutlined } from 'assets/svg/feature-bedroom.svg';
import { ReactComponent as BathroomsOutlined } from 'assets/svg/feature-bathroom.svg';
import { ReactComponent as Budget } from 'assets/svg/budget.svg';
import { ReactComponent as ConstructedArea } from 'assets/svg/feature-buildarea.svg';
import { ReactComponent as LocationOnOutlined } from 'assets/svg/LocationOn.svg';
import { ReactComponent as HomeIcon } from 'assets/svg/Default-Property-Image.svg';
import ClearFilterButton from './ClearFilterButton';
import { formatNumber } from 'util/Funcs';
import { useState, useEffect } from 'react';
import { LOCATION_DATA } from 'util/locationData';
import { PROPERTY_TYPE_GROUPS } from 'util/Constants';

interface MobileFilterSectionProps {
    title: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
}

export const MobileFilterSection = ({ title, icon, children }: MobileFilterSectionProps) => {
    return (
        <div className="bg-white rounded-xl p-4 space-y-3 border border-gray-100">
            <div className="flex items-center gap-2">
                {icon && <div className="text-my-blue">{icon}</div>}
                <h3 className="text-base font-semibold text-gray-900">{title}</h3>
            </div>
            {children}
        </div>
    );
};

interface MobileSimpleFilterProps {
    stringlist?: string[];
    placeholder: string;
    value: string;
    onChanged: (item: string) => void;
    showClearButton?: boolean;
    'data-cy'?: string;
}

export const MobileSimpleFilter = ({ value, onChanged, stringlist = [], placeholder, showClearButton = true, 'data-cy': dataCy }: MobileSimpleFilterProps) => {
    const clearFilterClicked = () => {
        onChanged('');
    };

    return (
        <div className="relative w-full">
            {!value && <div className="absolute top-0 left-0 w-full h-full flex items-center px-4 text-gray-500">{placeholder}</div>}

            <FormControl fullWidth>
                <Select
                    value={value}
                    onChange={(e) => onChanged(e.target.value)}
                    displayEmpty
                    renderValue={(selected) => selected}
                    className="!w-full !h-12 rounded-lg border border-gray-200 bg-white text-gray-900"
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                width: '100%',
                            },
                            className: 'mt-2 shadow-lg rounded-lg border border-gray-200',
                        },
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    sx={{
                        '& .MuiSelect-select': {
                            padding: '0 16px',
                            height: '48px !important',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                >
                    {stringlist.map((item: string, idx: number) => (
                        <MenuItem
                            value={item}
                            key={idx}
                            sx={{
                                height: '48px',
                                fontSize: '16px',
                                padding: '0 16px',
                                '&:hover': {
                                    backgroundColor: '#f3f4f6',
                                },
                                '&.Mui-selected': {
                                    backgroundColor: '#e5e7eb',
                                    '&:hover': {
                                        backgroundColor: '#d1d5db',
                                    },
                                },
                            }}
                            data-cy={dataCy}
                        >
                            {item}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {value && showClearButton && <ClearFilterButton onClicked={clearFilterClicked} />}
        </div>
    );
};

interface StepDefinition {
    max: number;
    step: number;
}

interface MobileRangeFilterProps {
    title: string;
    value: [number, number];
    onChange: (value: [number, number]) => void;
    min: number;
    max: number;
    steps: StepDefinition[];
    unit?: string;
    formatValue?: (value: number) => string;
}

export const MobileRangeFilter = ({
    title,
    value,
    onChange,
    min,
    max,
    steps,
    unit = '',
    formatValue = (v) => `${formatNumber(v)}${unit}`,
}: MobileRangeFilterProps) => {
    // Generate step values based on the step definitions
    const generateStepValues = () => {
        const stepValues: number[] = [];
        let currentValue = min;

        steps.forEach(({ max: stepMax, step }) => {
            while (currentValue <= stepMax) {
                stepValues.push(currentValue);
                currentValue += step;
            }
        });

        // Ensure the max value is included
        if (!stepValues.includes(max)) {
            stepValues.push(max);
        }

        return stepValues;
    };

    const stepValues = generateStepValues();

    // Map slider value to actual value
    const mapSliderToValue = (sliderValue: number) => {
        return stepValues[sliderValue];
    };

    // Map actual value to slider value
    const mapValueToSlider = (actualValue: number) => {
        return stepValues.findIndex((value) => value >= actualValue);
    };

    const handleSliderChange = (_: Event, newValue: number | number[]) => {
        const [minValue, maxValue] = newValue as number[];
        onChange([mapSliderToValue(minValue), mapSliderToValue(maxValue)]);
    };

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Typography className="text-sm text-gray-600">{title}</Typography>
                <div className="flex gap-2">
                    <span className="text-sm font-medium text-my-blue">{formatValue(value[0])}</span>
                    <span className="text-sm text-gray-400">-</span>
                    <span className="text-sm font-medium text-my-blue">{formatValue(value[1])}</span>
                </div>
            </div>
            <Slider
                value={[mapValueToSlider(value[0]), mapValueToSlider(value[1])]}
                onChange={handleSliderChange}
                min={0}
                max={stepValues.length - 1}
                step={1}
                className="!py-2"
                sx={{
                    '& .MuiSlider-thumb': {
                        width: 20,
                        height: 20,
                        backgroundColor: '#3176bc',
                        '&:hover, &.Mui-focusVisible': {
                            boxShadow: '0 0 0 8px rgba(49, 118, 188, 0.16)',
                        },
                    },
                    '& .MuiSlider-track': {
                        backgroundColor: '#3176bc',
                    },
                    '& .MuiSlider-rail': {
                        backgroundColor: '#e5e7eb',
                    },
                }}
            />
        </div>
    );
};

interface MobileBedBathFilterProps {
    bedIndices: number[];
    bathIndices: number[];
    onCallback: (beds: number[], baths: number[]) => void;
}

export const MobileBedBathFilter = ({ bedIndices, bathIndices, onCallback }: MobileBedBathFilterProps) => {
    const bedOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    const bathOptions = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

    const handleBedClick = (num: number) => {
        if (num === 0) {
            // If clicking "Any", only select "Any"
            onCallback([0], bathIndices);
        } else {
            // For other numbers, select this number and all numbers that come after it
            const newBedIndices = [];
            for (let i = num; i <= 10; i++) {
                newBedIndices.push(i);
            }
            onCallback(newBedIndices, bathIndices);
        }
    };

    const handleBathClick = (num: number) => {
        if (num === 0) {
            // If clicking "Any", only select "Any"
            onCallback(bedIndices, [0]);
        } else {
            // For other numbers, select this number and all numbers that come after it
            const newBathIndices = [];
            for (let i = num; i <= 10; i++) {
                newBathIndices.push(i);
            }
            onCallback(bedIndices, newBathIndices);
        }
    };

    return (
        <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <BedOutlined className="w-5 h-5 text-my-blue" />
                    <span className="text-sm font-medium text-gray-700">Bedrooms</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {bedOptions.map((num) => (
                        <button
                            key={num}
                            onClick={() => handleBedClick(num)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                bedIndices.includes(num) ? 'bg-my-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {num === 0 ? 'Any' : num}
                        </button>
                    ))}
                </div>
            </div>
            <div className="space-y-2">
                <div className="flex items-center gap-2">
                    <BathroomsOutlined className="w-5 h-5 text-my-blue" />
                    <span className="text-sm font-medium text-gray-700">Bathrooms</span>
                </div>
                <div className="flex flex-wrap gap-2">
                    {bathOptions.map((num) => (
                        <button
                            key={num}
                            onClick={() => handleBathClick(num)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                bathIndices.includes(num) ? 'bg-my-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                            {num === 0 ? 'Any' : num}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
};

interface MobileLocationFilterProps {
    value: {
        region?: string;
        locations?: Array<{
            city: string;
            neighborhoods: string[];
        }>;
    };
    onChanged: (value: {
        region?: string;
        locations?: Array<{
            city: string;
            neighborhoods: string[];
        }>;
    }) => void;
    placeholder?: string;
    showClearButton?: boolean;
}

interface SelectedLocations {
    regions: Set<string>;
    areas: Set<string>;
    neighborhoods: Set<string>;
}

export const MobileLocationFilter = ({ value, onChanged, placeholder = 'Select Location', showClearButton = true }: MobileLocationFilterProps) => {
    const [selectedLocations, setSelectedLocations] = useState<SelectedLocations>({
        regions: new Set(),
        areas: new Set(),
        neighborhoods: new Set(),
    });
    const [searchTerm, setSearchTerm] = useState('');

    // Initialize expanded states with all regions and areas
    const [expandedRegions] = useState<Set<string>>(() => new Set(LOCATION_DATA.map((region) => region.region)));
    const [expandedAreas] = useState<Set<string>>(() => new Set(LOCATION_DATA.flatMap((region) => region.areas.map((area) => area.area))));

    // Normalize text by removing accents and special characters
    const normalizeText = (text: string): string => {
        return text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9\s]/g, '');
    };

    useEffect(() => {
        if (value?.locations) {
            const newSelectedLocations: SelectedLocations = {
                regions: new Set(),
                areas: new Set(),
                neighborhoods: new Set(),
            };

            value.locations.forEach((location) => {
                const regionData = LOCATION_DATA.find((r) => r.areas.some((a) => a.area === location.city));

                if (regionData) {
                    newSelectedLocations.regions.add(regionData.region);
                    newSelectedLocations.areas.add(location.city);
                    location.neighborhoods.forEach((neighborhood) => {
                        newSelectedLocations.neighborhoods.add(neighborhood);
                    });
                }
            });

            setSelectedLocations(newSelectedLocations);
        } else {
            setSelectedLocations({
                regions: new Set(),
                areas: new Set(),
                neighborhoods: new Set(),
            });
        }
    }, [value]);

    const handleRegionClick = (event: React.MouseEvent, region: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedLocations = { ...selectedLocations };
        const regionData = LOCATION_DATA.find((r) => r.region === region);

        if (!regionData) return;

        const allAreasInRegion = regionData.areas;
        const allAreasSelected = allAreasInRegion.every((area) => newSelectedLocations.areas.has(area.area));

        if (allAreasSelected) {
            allAreasInRegion.forEach((area) => {
                newSelectedLocations.areas.delete(area.area);
                area.neighborhoods.forEach((neighborhood) => {
                    newSelectedLocations.neighborhoods.delete(neighborhood);
                });
            });
        } else {
            allAreasInRegion.forEach((area) => {
                newSelectedLocations.areas.add(area.area);
            });
        }

        setSelectedLocations(newSelectedLocations);
        updateFilter(newSelectedLocations);
    };

    const handleAreaClick = (event: React.MouseEvent, region: string, area: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedLocations = { ...selectedLocations };
        const regionData = LOCATION_DATA.find((r) => r.region === region);

        if (!regionData) return;

        if (newSelectedLocations.areas.has(area)) {
            newSelectedLocations.areas.delete(area);
            regionData.areas
                .find((a) => a.area === area)
                ?.neighborhoods.forEach((neighborhood) => {
                    newSelectedLocations.neighborhoods.delete(neighborhood);
                });
        } else {
            newSelectedLocations.areas.add(area);
        }

        setSelectedLocations(newSelectedLocations);
        updateFilter(newSelectedLocations);
    };

    const handleNeighborhoodClick = (event: React.MouseEvent, region: string, area: string, neighborhood: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedLocations = { ...selectedLocations };
        if (newSelectedLocations.neighborhoods.has(neighborhood)) {
            newSelectedLocations.neighborhoods.delete(neighborhood);
        } else {
            newSelectedLocations.neighborhoods.add(neighborhood);
            newSelectedLocations.areas.add(area);
        }

        setSelectedLocations(newSelectedLocations);
        updateFilter(newSelectedLocations);
    };

    const updateFilter = (locations: SelectedLocations) => {
        const locationObjects = Array.from(locations.areas).map((area) => {
            const areaData = LOCATION_DATA.flatMap((r) => r.areas).find((a) => a.area === area);
            if (!areaData) return { city: area, neighborhoods: [] };

            const neighborhoods = Array.from(locations.neighborhoods).filter((n) => areaData.neighborhoods.includes(n));

            return {
                city: area,
                neighborhoods: neighborhoods,
            };
        });

        onChanged({
            region: locationObjects[0]?.city,
            locations: locationObjects,
        });
    };

    const clearFilterClicked = () => {
        setSelectedLocations({
            regions: new Set(),
            areas: new Set(),
            neighborhoods: new Set(),
        });
        onChanged({});
    };

    const renderValue = () => {
        if (!value?.locations || value.locations.length === 0) {
            return placeholder;
        }

        const selectedItems = value.locations.map((loc) => `${loc.city}${loc.neighborhoods.length > 0 ? ` (${loc.neighborhoods.join(', ')})` : ''}`);

        return selectedItems.join(', ');
    };

    const matchesSearch = (text: string) => {
        const normalizedText = normalizeText(text);
        const normalizedSearch = normalizeText(searchTerm);
        return normalizedText.includes(normalizedSearch);
    };

    const filteredLocationData = searchTerm
        ? LOCATION_DATA.map((region) => {
              const filteredAreas = region.areas.map((area) => ({
                  ...area,
                  neighborhoods: area.neighborhoods.filter((neighborhood) => matchesSearch(neighborhood)),
              }));

              const matchingAreas = filteredAreas.filter((area) => matchesSearch(area.area) || area.neighborhoods.length > 0);

              if (matchesSearch(region.region) || matchingAreas.length > 0) {
                  return {
                      ...region,
                      areas: matchingAreas,
                  };
              }
              return null;
          }).filter((region): region is (typeof LOCATION_DATA)[0] => region !== null)
        : LOCATION_DATA;

    return (
        <div className="relative w-full">
            <FormControl fullWidth>
                <Select
                    multiple
                    value={Array.from(selectedLocations.areas)}
                    displayEmpty
                    renderValue={renderValue}
                    className="!w-full !h-12 rounded-lg border border-gray-200 bg-white text-gray-900"
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                width: '100%',
                            },
                            className: 'mt-2 shadow-lg rounded-lg border border-gray-200',
                        },
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    sx={{
                        '& .MuiSelect-select': {
                            padding: '0 16px',
                            height: '48px !important',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                >
                    <MenuItem
                        sx={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 1,
                            padding: '8px',
                            '&:hover': {
                                backgroundColor: 'white',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'white',
                            },
                            '&.Mui-focusVisible': {
                                backgroundColor: 'white',
                            },
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search locations..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    '& fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: 'white',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    '&:focus': {
                                        backgroundColor: 'white',
                                    },
                                },
                            }}
                        />
                    </MenuItem>

                    {filteredLocationData.map((regionData) => {
                        const filteredAreas = regionData.areas.filter(
                            (area) => matchesSearch(area.area) || area.neighborhoods.some((neighborhood) => matchesSearch(neighborhood)),
                        );
                        const selectedAreasInRegion = regionData.areas.filter((area) => selectedLocations.areas.has(area.area));
                        const totalAreasInRegion = regionData.areas.length;
                        const hasSelectedAreas = selectedAreasInRegion.length > 0;
                        const allAreasSelected = selectedAreasInRegion.length === totalAreasInRegion;

                        return (
                            <div key={regionData.region}>
                                <MenuItem
                                    onClick={(e) => handleRegionClick(e, regionData.region)}
                                    sx={{
                                        backgroundColor: hasSelectedAreas ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        },
                                    }}
                                >
                                    <Checkbox
                                        checked={allAreasSelected}
                                        indeterminate={hasSelectedAreas && !allAreasSelected}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleRegionClick(e, regionData.region);
                                        }}
                                        sx={{
                                            padding: '0 8px 0 0',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '20px',
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={regionData.region}
                                        sx={{
                                            m: 0,
                                            '& .MuiTypography-root': {
                                                fontWeight: hasSelectedAreas ? 600 : 400,
                                            },
                                        }}
                                    />
                                </MenuItem>

                                {filteredAreas.map((area) => (
                                    <div key={area.area}>
                                        <MenuItem
                                            onClick={(e) => handleAreaClick(e, regionData.region, area.area)}
                                            sx={{
                                                pl: 4,
                                                backgroundColor: selectedLocations.areas.has(area.area) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                },
                                            }}
                                        >
                                            <Checkbox
                                                checked={selectedLocations.areas.has(area.area)}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleAreaClick(e, regionData.region, area.area);
                                                }}
                                                sx={{
                                                    padding: '0 8px 0 0',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                    },
                                                    '& .MuiSvgIcon-root': {
                                                        fontSize: '20px',
                                                    },
                                                }}
                                            />
                                            <ListItemText
                                                primary={area.area}
                                                sx={{
                                                    m: 0,
                                                    '& .MuiTypography-root': {
                                                        fontWeight: selectedLocations.areas.has(area.area) ? 600 : 400,
                                                    },
                                                }}
                                            />
                                        </MenuItem>

                                        {area.neighborhoods.map((neighborhood) => (
                                            <MenuItem
                                                key={neighborhood}
                                                onClick={(e) => handleNeighborhoodClick(e, regionData.region, area.area, neighborhood)}
                                                sx={{
                                                    pl: 8,
                                                    backgroundColor: selectedLocations.neighborhoods.has(neighborhood) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                                    },
                                                }}
                                            >
                                                <Checkbox
                                                    checked={selectedLocations.neighborhoods.has(neighborhood)}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleNeighborhoodClick(e, regionData.region, area.area, neighborhood);
                                                    }}
                                                    sx={{
                                                        padding: '0 8px 0 0',
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                        '& .MuiSvgIcon-root': {
                                                            fontSize: '20px',
                                                        },
                                                    }}
                                                />
                                                <ListItemText
                                                    primary={neighborhood}
                                                    sx={{
                                                        m: 0,
                                                        '& .MuiTypography-root': {
                                                            fontWeight: selectedLocations.neighborhoods.has(neighborhood) ? 600 : 400,
                                                        },
                                                    }}
                                                />
                                            </MenuItem>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </Select>
            </FormControl>

            {value?.locations && value.locations.length > 0 && showClearButton && <ClearFilterButton onClicked={clearFilterClicked} />}
        </div>
    );
};

interface MobilePropertyTypeFilterProps {
    value: string[];
    onChanged: (value: string[]) => void;
    placeholder?: string;
    showClearButton?: boolean;
}

export const MobilePropertyTypeFilter = ({ value, onChanged, placeholder = 'Select property type', showClearButton = true }: MobilePropertyTypeFilterProps) => {
    const [selectedTypes, setSelectedTypes] = useState<Set<string>>(new Set(value));
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        setSelectedTypes(new Set(value));
    }, [value]);

    const handleChange = (event: any) => {
        const selectedValue = event.target.value;
        const newSelectedValues = Array.isArray(selectedValue) ? selectedValue : [selectedValue];
        setSelectedTypes(new Set(newSelectedValues));
        onChanged(newSelectedValues);
    };

    const handleGroupClick = (event: React.MouseEvent, group: (typeof PROPERTY_TYPE_GROUPS)[0]) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedTypes = new Set(selectedTypes);
        const allSelected = group.types.every((type) => newSelectedTypes.has(type));

        if (allSelected) {
            group.types.forEach((type) => newSelectedTypes.delete(type));
        } else {
            group.types.forEach((type) => newSelectedTypes.add(type));
        }

        setSelectedTypes(newSelectedTypes);
        onChanged(Array.from(newSelectedTypes));
    };

    const handleTypeClick = (event: React.MouseEvent, type: string) => {
        event.preventDefault();
        event.stopPropagation();

        const newSelectedTypes = new Set(selectedTypes);
        if (newSelectedTypes.has(type)) {
            newSelectedTypes.delete(type);
        } else {
            newSelectedTypes.add(type);
        }

        setSelectedTypes(newSelectedTypes);
        onChanged(Array.from(newSelectedTypes));
    };

    const clearFilterClicked = () => {
        setSelectedTypes(new Set());
        onChanged([]);
    };

    const renderValue = () => {
        if (selectedTypes.size === 0) {
            return placeholder;
        }
        return Array.from(selectedTypes).join(', ');
    };

    const matchesSearch = (text: string) => {
        const normalizedText = text
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        const normalizedSearch = searchTerm
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        return normalizedText.includes(normalizedSearch);
    };

    const filteredGroups = searchTerm
        ? PROPERTY_TYPE_GROUPS.map((group) => ({
              ...group,
              types: group.types.filter((type) => matchesSearch(type) || matchesSearch(group.name)),
          })).filter((group) => group.types.length > 0 || matchesSearch(group.name))
        : PROPERTY_TYPE_GROUPS;

    return (
        <div className="relative w-full">
            <FormControl fullWidth>
                <Select
                    multiple
                    value={Array.from(selectedTypes)}
                    onChange={handleChange}
                    displayEmpty
                    renderValue={renderValue}
                    className="!w-full !h-12 rounded-lg border border-gray-200 bg-white text-gray-900"
                    MenuProps={{
                        PaperProps: {
                            style: {
                                maxHeight: 300,
                                width: '100%',
                            },
                            className: 'mt-2 shadow-lg rounded-lg border border-gray-200',
                        },
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        },
                    }}
                    sx={{
                        '& .MuiSelect-select': {
                            padding: '0 16px',
                            height: '48px !important',
                            display: 'flex',
                            alignItems: 'center',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                        },
                    }}
                >
                    <MenuItem
                        sx={{
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 1,
                            padding: '8px',
                            '&:hover': {
                                backgroundColor: 'white',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'white',
                            },
                            '&.Mui-focusVisible': {
                                backgroundColor: 'white',
                            },
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search property types..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: 'white',
                                    '& fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#DFE1E1',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: 'white',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    '&:focus': {
                                        backgroundColor: 'white',
                                    },
                                },
                            }}
                        />
                    </MenuItem>

                    {filteredGroups.map((group) => {
                        const selectedTypesInGroup = group.types.filter((type) => selectedTypes.has(type));
                        const hasSelectedTypes = selectedTypesInGroup.length > 0;
                        const allTypesSelected = selectedTypesInGroup.length === group.types.length;

                        return (
                            <div key={group.name}>
                                <MenuItem
                                    onClick={(e) => handleGroupClick(e, group)}
                                    sx={{
                                        backgroundColor: hasSelectedTypes ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                        '&:hover': {
                                            backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                        },
                                    }}
                                >
                                    <Checkbox
                                        checked={allTypesSelected}
                                        indeterminate={hasSelectedTypes && !allTypesSelected}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupClick(e, group);
                                        }}
                                        sx={{
                                            padding: '0 8px 0 0',
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                            '& .MuiSvgIcon-root': {
                                                fontSize: '20px',
                                            },
                                        }}
                                    />
                                    <ListItemText
                                        primary={group.name}
                                        sx={{
                                            m: 0,
                                            '& .MuiTypography-root': {
                                                fontWeight: hasSelectedTypes ? 600 : 400,
                                            },
                                        }}
                                    />
                                </MenuItem>

                                {group.types.map((type) => (
                                    <MenuItem
                                        key={type}
                                        onClick={(e) => handleTypeClick(e, type)}
                                        sx={{
                                            pl: 4,
                                            backgroundColor: selectedTypes.has(type) ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                                            '&:hover': {
                                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                            },
                                        }}
                                    >
                                        <Checkbox
                                            checked={selectedTypes.has(type)}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleTypeClick(e, type);
                                            }}
                                            sx={{
                                                padding: '0 8px 0 0',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: '20px',
                                                },
                                            }}
                                        />
                                        <ListItemText
                                            primary={type}
                                            sx={{
                                                m: 0,
                                                '& .MuiTypography-root': {
                                                    fontWeight: selectedTypes.has(type) ? 600 : 400,
                                                },
                                            }}
                                        />
                                    </MenuItem>
                                ))}
                            </div>
                        );
                    })}
                </Select>
            </FormControl>

            {selectedTypes.size > 0 && showClearButton && <ClearFilterButton onClicked={clearFilterClicked} />}
        </div>
    );
};
